import { Bike, Clock, ShoppingBag, Store } from 'lucide-react'
import { formatarReais, StatusPedido, TipoEntrega, type CanalPedido } from '@du/shared'
import { cn } from '../lib/cn.js'
import { Badge } from './Badge.js'
import { Icone } from './Icone.js'
import { OrderStatusPill } from './OrderStatusPill.js'

/**
 * Cartão do Kanban. Projetado para ser lido a um metro de distância, de
 * relance, por alguém com a mão ocupada:
 *  - número do pedido no maior tamanho da tela
 *  - cronômetro sempre visível
 *  - atraso muda a borda inteira, não só um texto pequeno
 *  - "novo" pulsa até alguém aceitar
 */
export type OrderCardProps = {
  numeroDia: number
  status: StatusPedido
  canal: CanalPedido
  tipo: TipoEntrega
  clienteNome: string
  bairroNome?: string | null | undefined
  minutosDesde: number
  atrasadoMin?: number | undefined
  totalCents?: number | undefined
  itens: {
    nome: string
    quantidade: number
    /** Escolhas e adicionais: pão, ponto, bacon extra. */
    detalhes?: string[] | undefined
    /** Recado do cliente para aquele item. */
    observacao?: string | null | undefined
  }[]
  onClick?: () => void | undefined
  className?: string | undefined
}

const ICONE_TIPO = { ENTREGA: Bike, RETIRADA: ShoppingBag, BALCAO: Store }

export function OrderCard({
  numeroDia, status, canal, tipo, clienteNome, bairroNome,
  minutosDesde, atrasadoMin, totalCents, itens, onClick, className,
}: OrderCardProps) {
  const novo = status === StatusPedido.RECEBIDO
  const atrasado = Boolean(atrasadoMin && atrasadoMin > 0)

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('du-pedido', novo && 'du-pedido--novo', atrasado && 'du-pedido--atrasado', className)}
      aria-label={`Pedido ${numeroDia}, ${clienteNome}, ${status}${atrasado ? `, atrasado ${atrasadoMin} minutos` : ''}`}
    >
      <span className="du-pedido__topo">
        <span className="du-pedido__numero">#{String(numeroDia).padStart(3, '0')}</span>
        <OrderStatusPill status={status} {...(atrasadoMin ? { atrasadoMin } : {})} />
      </span>

      <span className="du-pedido__meta">
        <Icone de={ICONE_TIPO[tipo]} tamanho={18} />
        <span>{tipo === TipoEntrega.ENTREGA ? (bairroNome ?? 'Entrega') : tipo}</span>
        <Badge tom="neutro">{canal}</Badge>
      </span>

      <span className="du-pedido__cliente">{clienteNome}</span>

      <span className="du-pedido__itens">
        {itens.slice(0, 3).map((i, n) => (
          <span key={n} className="du-pedido__item">
            <b>
              {i.quantidade}× {i.nome}
            </b>
            {/* Escolhas e recado entram COM destaque, não como nota de rodapé:
                é o que decide qual pão vai na chapa, e quem lê está de pé,
                a um metro da tela, com a mão ocupada. */}
            {i.detalhes && i.detalhes.length > 0 ? (
              <em className="du-pedido__escolha">{i.detalhes.join(' · ')}</em>
            ) : null}
            {i.observacao ? (
              <em className="du-pedido__obs">{i.observacao}</em>
            ) : null}
          </span>
        ))}
        {itens.length > 3 ? <span className="du-pedido__mais">+{itens.length - 3} itens</span> : null}
      </span>

      <span className="du-pedido__rodape">
        <span className="du-pedido__tempo">
          <Icone de={Clock} tamanho={16} />
          {minutosDesde} min
        </span>
        {totalCents != null ? (
          <span className="du-pedido__total">{formatarReais(totalCents)}</span>
        ) : null}
      </span>
    </button>
  )
}
